import React from "react";
import { StyleSheet, View } from "react-native";
import type { ExternalCourse } from "@axio/graphql/generated/graphql";
import AdminTable from "../AdminTable/AdminTable";
import AdminTablePagination from "../AdminTable/AdminTablePagination";
import FilterDropdown from "../../common/TableControls/FilterDropdown/FilterDropdown";
import CustomSearchInput from "../../common/general/CustomSearchInput/CustomSearchInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";

type FilterItem = {
  label: string;
  value: string;
};

export type CourseModernizerCourseRow = {
  course: ExternalCourse;
  statusLabel: string;
  scoreLabel: string;
  canEvaluate: boolean;
  canViewResults: boolean;
  canRerun: boolean;
  activeModernizedCourseId: string | null;
};

type CourseModernizerCoursesTableProps = {
  isAdmin: boolean;
  loading: boolean;
  visibleCourses: CourseModernizerCourseRow[];
  totalItems: number;
  page: number;
  pageSize: number;
  facultyFilter: string;
  facultyFilterDropdownOpen: boolean;
  termFilter: string;
  termFilterDropdownOpen: boolean;
  subjectFilter: string;
  subjectFilterDropdownOpen: boolean;
  programFilter: string;
  programFilterDropdownOpen: boolean;
  searchQuery: string;
  facultyOptions: FilterItem[];
  termItems: FilterItem[];
  subjectItems: FilterItem[];
  programItems: FilterItem[];
  onFacultyFilterChange: (value: string) => void;
  setFacultyFilterDropdownOpen: (open: boolean) => void;
  onTermFilterChange: (value: string) => void;
  setTermFilterDropdownOpen: (open: boolean) => void;
  onSubjectFilterChange: (value: string) => void;
  setSubjectFilterDropdownOpen: (open: boolean) => void;
  onProgramFilterChange: (value: string) => void;
  setProgramFilterDropdownOpen: (open: boolean) => void;
  onSearchQueryChange: (value: string) => void;
  onEvaluateCourse: (course: ExternalCourse) => void;
  onViewResults: (courseId: string) => void;
  onRerunCourse: (course: ExternalCourse) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (pageSize: number) => void;
};

function CourseModernizerCoursesTable({
  isAdmin,
  loading,
  visibleCourses,
  totalItems,
  page,
  pageSize,
  facultyFilter,
  facultyFilterDropdownOpen,
  termFilter,
  termFilterDropdownOpen,
  subjectFilter,
  subjectFilterDropdownOpen,
  programFilter,
  programFilterDropdownOpen,
  searchQuery,
  facultyOptions,
  termItems,
  subjectItems,
  programItems,
  onFacultyFilterChange,
  setFacultyFilterDropdownOpen,
  onTermFilterChange,
  setTermFilterDropdownOpen,
  onSubjectFilterChange,
  setSubjectFilterDropdownOpen,
  onProgramFilterChange,
  setProgramFilterDropdownOpen,
  onSearchQueryChange,
  onEvaluateCourse,
  onViewResults,
  onRerunCourse,
  onPageChange,
  onItemsPerPageChange,
}: CourseModernizerCoursesTableProps) {
  return (
    <>
      <AdminTable>
        <View style={styles.tableControlsRow}>
          <View style={styles.filtersRow}>
            {isAdmin && (
              <FilterDropdown
                filterValue={facultyFilter}
                setFilterValue={onFacultyFilterChange}
                filterDropdownOpen={facultyFilterDropdownOpen}
                setFilterDropdownOpen={setFacultyFilterDropdownOpen}
                filterItems={facultyOptions}
                placeholder="All Faculty"
              />
            )}
            <FilterDropdown
              filterValue={termFilter}
              setFilterValue={onTermFilterChange}
              filterDropdownOpen={termFilterDropdownOpen}
              setFilterDropdownOpen={setTermFilterDropdownOpen}
              filterItems={termItems}
              placeholder="All Terms"
            />
            <FilterDropdown
              filterValue={subjectFilter}
              setFilterValue={onSubjectFilterChange}
              filterDropdownOpen={subjectFilterDropdownOpen}
              setFilterDropdownOpen={setSubjectFilterDropdownOpen}
              filterItems={subjectItems}
              placeholder="All Subjects"
            />
            <FilterDropdown
              filterValue={programFilter}
              setFilterValue={onProgramFilterChange}
              filterDropdownOpen={programFilterDropdownOpen}
              setFilterDropdownOpen={setProgramFilterDropdownOpen}
              filterItems={programItems}
              placeholder="All Programs"
            />
          </View>
          <View style={styles.searchInputWrapper}>
            <CustomSearchInput
              placeholder={isAdmin ? "Search courses, codes, or faculty" : "Search courses or codes"}
              searchQuery={searchQuery}
              setSearchQuery={onSearchQueryChange}
            />
          </View>
        </View>
        <View style={styles.tableHeaderRow}>
          <CustomText
            text="Course Name"
            size="xs"
            weight="medium"
            style={[styles.tableHeaderCell, styles.courseColumn]}
            useTranslationText={false}
          />
          <CustomText
            text="Term"
            size="xs"
            weight="medium"
            style={[styles.tableHeaderCell, styles.termColumn]}
            useTranslationText={false}
          />
          <CustomText
            text="Faculty"
            size="xs"
            weight="medium"
            style={[styles.tableHeaderCell, styles.facultyColumn]}
            useTranslationText={false}
          />
          <CustomText
            text="Status"
            size="xs"
            weight="medium"
            style={[styles.tableHeaderCell, styles.statusColumn]}
            useTranslationText={false}
          />
          <CustomText
            text="Score"
            size="xs"
            weight="medium"
            style={[styles.tableHeaderCell, styles.scoreColumn]}
            useTranslationText={false}
          />
          <CustomText
            text="Actions"
            size="xs"
            weight="medium"
            style={[styles.tableHeaderCell, styles.actionsColumn]}
            useTranslationText={false}
          />
        </View>

        {loading ? (
          <View style={styles.emptyState}>
            <CustomText text="Loading external courses..." size="s" useTranslationText={false} />
          </View>
        ) : visibleCourses.length === 0 ? (
          <View style={styles.emptyState}>
            <CustomText text="No external courses found" size="s" useTranslationText={false} />
          </View>
        ) : (
          visibleCourses.map((row) => (
            <View key={row.course.id} style={styles.tableRow}>
              <View style={[styles.tableCell, styles.courseColumn]}>
                <CustomText
                  text={row.course.name || "Untitled course"}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                />
                <CustomText
                  text={row.course.courseCode || row.course.providerCourseId || "-"}
                  size="xs"
                  style={styles.secondaryText}
                  useTranslationText={false}
                />
              </View>
              <View style={[styles.tableCell, styles.termColumn]}>
                <CustomText text="-" size="s" useTranslationText={false} />
              </View>
              <View style={[styles.tableCell, styles.facultyColumn]}>
                <CustomText
                  text={
                    row.course.users
                      ?.map((faculty) => faculty?.name)
                      .filter(Boolean)
                      .join(", ") || "-"
                  }
                  size="s"
                  useTranslationText={false}
                />
              </View>
              <View style={[styles.tableCell, styles.statusColumn]}>
                <CustomText text={row.statusLabel} size="s" useTranslationText={false} />
              </View>
              <View style={[styles.tableCell, styles.scoreColumn]}>
                <CustomText text={row.scoreLabel} size="s" useTranslationText={false} />
              </View>
              <View style={[styles.tableCell, styles.actionsColumn]}>
                <View style={styles.rowActions}>
                  {row.canEvaluate ? (
                    <CustomButton
                      text="Evaluate"
                      styleType="lightStroke"
                      size="s"
                      useTranslationText={false}
                      onPress={() => onEvaluateCourse(row.course)}
                    />
                  ) : null}
                  {row.canViewResults && row.activeModernizedCourseId ? (
                    <CustomButton
                      text="View Results"
                      styleType="primaryTransparentStroke"
                      size="s"
                      useTranslationText={false}
                      onPress={() => onViewResults(row.activeModernizedCourseId)}
                    />
                  ) : null}
                  {row.canRerun ? (
                    <CustomButton
                      text="Rerun"
                      styleType="lightStroke"
                      size="s"
                      useTranslationText={false}
                      onPress={() => onRerunCourse(row.course)}
                    />
                  ) : null}
                </View>
              </View>
            </View>
          ))
        )}
      </AdminTable>

      {!loading && totalItems > 0 && (
        <AdminTablePagination
          currentPage={page}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
          navigationStyle="icons"
          containerStyle={styles.pagination}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  tableControlsRow: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },
  filtersRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    flex: 1,
  },
  searchInputWrapper: {
    width: 320,
    maxWidth: "40%",
    minWidth: 260,
  },
  tableHeaderRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
  },
  tableHeaderCell: {
    color: "#475467",
  },
  tableCell: {
    justifyContent: "center",
  },
  courseColumn: {
    flex: 2.4,
  },
  termColumn: {
    flex: 1.2,
  },
  facultyColumn: {
    flex: 1.8,
  },
  statusColumn: {
    flex: 1.1,
  },
  scoreColumn: {
    flex: 0.8,
  },
  actionsColumn: {
    flex: 1.7,
  },
  secondaryText: {
    color: "#667085",
  },
  rowActions: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  emptyState: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  pagination: {
    marginTop: 16,
    borderTopWidth: 0,
  },
});

export default CourseModernizerCoursesTable;
